import { Icon } from '@iconify/react/dist/iconify.js';
import { useState, useEffect } from 'react';
import { useEquipment } from '../../contexts/EquipmentContext';
import NewReviewEquipment from './NewReviewEquipment';

// Reusable InputField component for new equipment
const InputField = ({ label, value, onChange, fieldKey, placeholder = "Enter value..." }) => {
    return (
        <div className="space-y-2">
            <label className="text-xs text-font-secondary font-semibold">{label}</label>
            <input
                type="text"
                value={value || ''}
                onChange={(e) => onChange(fieldKey, e.target.value)}
                placeholder={placeholder}
                className="w-full rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
        </div>
    );
};

const NewEquipment = ({ onBack, initialParentId }) => {
    const { equipmentData } = useEquipment();
    const [selectedParentId, setSelectedParentId] = useState('');
    const [parentData, setParentData] = useState({});
    const [childData, setChildData] = useState({});
    const [isCreatingNewParent, setIsCreatingNewParent] = useState(false);
    const [isReviewMode, setIsReviewMode] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    
    // Get the selected parent category object
    const selectedParent = equipmentData?.categories?.find(cat => cat.parentEqpId === selectedParentId);

    // Set initial selections when component mounts or props change
    useEffect(() => {
        if (initialParentId) {
            setSelectedParentId(initialParentId);
        }
    }, [initialParentId]);

    const handleParentChange = (fieldKey, value) => {
        setParentData(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleChildChange = (fieldKey, value) => {
        setChildData(prev => ({ ...prev, [fieldKey]: value }));
    };

    const handleNewParent = () => {
        setIsCreatingNewParent(true);
        setSelectedParentId('');
        setParentData({});
    };

    const handleNewChild = () => {
        // Initialize child data with empty values to trigger the display
        setChildData({
            childCategory: '',
            parentCategory: selectedParent?.parentCategory || '',
            model: '',
            customerReference: '',
            brand: '',
            commonSynonyms: '',
            keySellingPoints: '',
            description: '',
            pricing: ''
        });
    };

    const handleReview = () => {
        setIsReviewMode(true);
    };

    const handleBackToEdit = () => {
        setIsReviewMode(false);
    };

    const handleExitAttempt = () => {
        if (Object.keys(parentData).filter(key => parentData[key]).length > 0 || Object.keys(childData).filter(key => childData[key]).length > 0) {
            setShowExitConfirm(true);
        } else {
            onBack();
        }
    };

    const handleConfirmExit = () => {
        setShowExitConfirm(false);
        onBack();
    };

    const handleCancelExit = () => {
        setShowExitConfirm(false);
    };

    // If in review mode, show the NewReviewEquipment component
    if (isReviewMode) {
        return (
            <NewReviewEquipment
                onBack={handleBackToEdit}
                onSuccess={onBack}
                parentData={parentData}
                childData={childData}
                selectedParent={selectedParent}
            />
        );
    }

    return (
        <div className="responsive-page-layout scrollable">
            {/* Header */}
            <div className="flex flex-row justify-between items-center w-full z-10 transition-all duration-700 ease-out">
                <div className="flex flex-row gap-2 items-start text-2xl">
                    <Icon icon="proicons:box-add" className="text-primary text-3xl"></Icon>
                    New Equipment
                </div>
                <div className="flex flex-row gap-2">
                    <button 
                        onClick={handleExitAttempt}
                        className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-primary transition-colors duration-300"
                    >
                        <Icon icon="mdi:arrow-left" className="text-xl text-primary group-hover:text-primary" />
                        <span className="text-sm font-medium group-hover:text-font-primary">Back to Inventory</span>
                    </button>
                    
                    {/* Review button - only shows when there are changes */}
                    {(Object.keys(parentData).filter(key => parentData[key]).length > 0 || Object.keys(childData).filter(key => childData[key]).length > 0) && (
                        <button 
                            onClick={handleReview}
                            className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-accent hover:text-background transition-colors duration-300"
                        >
                            <Icon icon="mdi:eye" className="text-xl text-accent group-hover:text-background" />
                            <span className="text-sm font-medium group-hover:text-background">Review</span>
                        </button>
                    )}
                </div>
            </div>
            
            {/* Dropdown Selectors */}
            <div className="new-equipment-selectors-responsive flex flex-col lg:flex-row items-center justify-between w-full gap-4">
                <div className="flex flex-col items-start gap-2 w-full lg:w-1/2">
                    <div className="flex flex-row items-center gap-1">
                        <Icon icon="duo-icons:box-2" className="text-2xl text-primary" />
                        <label htmlFor="select-category" className="text-sm text-font-primary font-semibold whitespace-nowrap">Select Parent</label>
                    </div>
                    <div className="flex gap-2 w-full">
                        <select 
                            id="select-category" 
                            value={selectedParentId}
                            onChange={(e) => {
                                setSelectedParentId(e.target.value);
                                setIsCreatingNewParent(false);
                            }}
                            className="flex-1 rounded-md bg-bgSecondary input-container p-2 text-sm focus:outline-none"
                        >
                            <option value="">Select a category</option>
                            {equipmentData?.categories?.map((category) => (
                                <option key={category.parentEqpId} value={category.parentEqpId}>
                                    {category.parentCategory || 'Unnamed Category'}
                                </option>
                            ))}
                        </select>
                        <button 
                            onClick={handleNewParent}
                            className="flex flex-row gap-2 items-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-accent hover:text-background transition-colors duration-300"
                        >
                            <Icon icon="mdi:plus" className="text-xl text-accent group-hover:text-background" />
                            <span className="text-sm font-medium group-hover:text-background">New</span>
                        </button>
                    </div>
                </div>
                <div className="flex flex-col items-start gap-2 w-full lg:w-1/2">
                    <div className="flex flex-row items-center gap-1">
                        <Icon icon="ix:box-open" className={`text-2xl ${(selectedParentId || isCreatingNewParent) ? 'text-primary' : 'text-font-secondary'}`} />
                        <label className={`text-sm text-font-primary font-semibold whitespace-nowrap ${(selectedParentId || isCreatingNewParent) ? 'text-accent' : 'text-font-secondary'}`}>Child Item</label>
                    </div>
                    <button 
                        onClick={handleNewChild}
                        disabled={!selectedParentId && !isCreatingNewParent}
                        className="w-full flex flex-row gap-2 items-center justify-center text-font-primary bg-bgSecondary button-container px-3 py-2 rounded-lg text-sm group hover:bg-accent hover:text-background transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Icon icon="mdi:plus" className="text-xl text-accent group-hover:text-background" />
                        <span className="text-sm font-medium group-hover:text-background">New</span>
                    </button>
                </div>
            </div>

            {/* Fields Container */}
            {!selectedParent && !isCreatingNewParent && Object.keys(childData).length === 0 ? (
                // Empty state when nothing is selected or created yet
                <div className="flex flex-col items-center justify-center h-full w-full py-16">
                    <Icon icon="proicons:box-add" className="text-8xl text-font-secondary mb-4" />
                    <p className="text-2xl text-font-secondary font-medium">Select or Add a Parent Category</p>
                </div>
            ) : (
                <div className="new-equipment-fields-responsive flex flex-col lg:flex-row w-full gap-4">
                    {/* Parent Category Fields */}
                    {(selectedParent || isCreatingNewParent) && (
                        <div className={`flex flex-col items-start gap-2 w-full ${Object.keys(childData).length > 0 ? 'lg:w-1/2' : 'lg:w-full'}`}>
                            <div className="flex flex-row items-center gap-1 justify-between w-full">
                                <div className="flex flex-row items-center gap-1">
                                    <Icon icon="duo-icons:box-2" className="text-2xl text-accent" />
                                    <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Parent Details</label>
                                </div>
                                <div className="flex flex-row items-center gap-1">
                                    <label className="text-xs text-font-secondary font-medium">Parent ID:</label>
                                    <p className="text-sm text-font-primary font-mono font-medium">{selectedParent?.parentEqpId || 'New'}</p>
                                </div>
                            </div>
                            <div className="w-full rounded-lg p-2 flex flex-col gap-3">
                                {isCreatingNewParent ? (
                                    // Show input fields for new parent
                                    <>
                                        <InputField
                                            label="Category Name"
                                            value={parentData.parentCategory || ''}
                                            onChange={handleParentChange}
                                            fieldKey="parentCategory"
                                            placeholder="Enter category name..."
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <InputField
                                            label="Typical Uses"
                                            value={parentData.typicalUses || ''}
                                            onChange={handleParentChange}
                                            fieldKey="typicalUses"
                                            placeholder="Enter typical uses (comma separated)..."
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <InputField
                                            label="Typical Flow"
                                            value={parentData.typicalFlow || ''}
                                            onChange={handleParentChange}
                                            fieldKey="typicalFlow"
                                            placeholder="Enter typical flow..."
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <InputField
                                            label="Defaults"
                                            value={parentData.defaults || ''}
                                            onChange={handleParentChange}
                                            fieldKey="defaults"
                                            placeholder="Enter defaults..."
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <InputField
                                            label="Qualifying Questions"
                                            value={parentData.qualifyingQuestions || ''}
                                            onChange={handleParentChange}
                                            fieldKey="qualifyingQuestions"
                                            placeholder="Enter qualifying questions..."
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <InputField
                                            label="Special Notes"
                                            value={parentData.specialNotes || ''}
                                            onChange={handleParentChange}
                                            fieldKey="specialNotes"
                                            placeholder="Enter special notes..."
                                        />
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <InputField
                                            label="Pricing Considerations"
                                            value={parentData.pricingConsiderations || ''}
                                            onChange={handleParentChange}
                                            fieldKey="pricingConsiderations"
                                            placeholder="Enter pricing considerations..."
                                        />
                                    </>
                                ) : (
                                    // Show read-only details for selected parent
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs text-font-secondary font-medium">Category Name</label>
                                            <p className="text-sm text-font-primary">{selectedParent?.parentCategory || 'N/A'}</p>
                                        </div>
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <div className="space-y-2">
                                            <label className="text-xs text-font-secondary font-medium">Typical Uses</label>
                                            <p className="text-sm text-font-primary">{selectedParent?.typicalUses || 'N/A'}</p>
                                        </div>
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <div className="space-y-2">
                                            <label className="text-xs text-font-secondary font-medium">Typical Flow</label>
                                            <p className="text-sm text-font-primary">{selectedParent?.typicalFlow || 'N/A'}</p>
                                        </div>
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <div className="space-y-2">
                                            <label className="text-xs text-font-secondary font-medium">Defaults</label>
                                            <p className="text-sm text-font-primary">{selectedParent?.defaults || 'N/A'}</p>
                                        </div>
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <div className="space-y-2">
                                            <label className="text-xs text-font-secondary font-medium">Qualifying Questions</label>
                                            <p className="text-sm text-font-primary">{selectedParent?.qualifyingQuestions || 'N/A'}</p>
                                        </div>
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <div className="space-y-2">
                                            <label className="text-xs text-font-secondary font-medium">Special Notes</label>
                                            <p className="text-sm text-font-primary">{selectedParent?.specialNotes || 'N/A'}</p>
                                        </div>
                                        <hr className="w-full border-t-2 border-bgAccent" />
                                        <div className="space-y-2">
                                            <label className="text-xs text-font-secondary font-medium">Pricing Considerations</label>
                                            <p className="text-sm text-font-primary">{selectedParent?.pricingConsiderations || 'N/A'}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Child Item Fields */}
                    {Object.keys(childData).length > 0 && (
                        <div className="w-full lg:w-1/2 flex flex-col items-start gap-3">
                            <div className="flex flex-row items-center gap-1 justify-between w-full">
                                <div className="flex flex-row items-center gap-1">
                                    <Icon icon="ix:box-open" className="text-2xl text-accent" />
                                    <label className="text-sm text-font-primary font-semibold whitespace-nowrap">Child Details</label>
                                </div>
                                <div className="flex flex-row items-center gap-1">
                                    <label className="text-xs text-font-secondary font-medium">Child ID:</label>
                                    <p className="text-sm text-font-primary font-mono font-medium">New</p>
                                </div>
                            </div>
                            <div className="w-full rounded-lg p-2 flex flex-col gap-2">
                                <InputField
                                    label="Item Name"
                                    value={childData.childCategory || ''}
                                    onChange={handleChildChange}
                                    fieldKey="childCategory"
                                    placeholder="Enter item name..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Parent Category"
                                    value={selectedParent?.parentCategory || ''}
                                    onChange={handleChildChange}
                                    fieldKey="parentCategory"
                                    placeholder="Enter parent category..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Model"
                                    value={childData.model || ''}
                                    onChange={handleChildChange}
                                    fieldKey="model"
                                    placeholder="Enter model..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Customer Reference"
                                    value={childData.customerReference || ''}
                                    onChange={handleChildChange}
                                    fieldKey="customerReference"
                                    placeholder="Enter customer reference..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Brand"
                                    value={childData.brand || ''}
                                    onChange={handleChildChange}
                                    fieldKey="brand"
                                    placeholder="Enter brand..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Common Synonyms"
                                    value={childData.commonSynonyms || ''}
                                    onChange={handleChildChange}
                                    fieldKey="commonSynonyms"
                                    placeholder="Enter common synonyms (comma separated)..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Key Selling Points"
                                    value={childData.keySellingPoints || ''}
                                    onChange={handleChildChange}
                                    fieldKey="keySellingPoints"
                                    placeholder="Enter key selling points..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Description"
                                    value={childData.description || ''}
                                    onChange={handleChildChange}
                                    fieldKey="description"
                                    placeholder="Enter description..."
                                />
                                <hr className="w-full border-t-2 border-bgAccent" />
                                <InputField
                                    label="Pricing"
                                    value={childData.pricing || ''}
                                    onChange={handleChildChange}
                                    fieldKey="pricing"
                                    placeholder="Enter pricing (JSON format)..."
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Exit Confirmation Dialog */}
            {showExitConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <Icon icon="mdi:alert-circle" className="text-accent text-2xl" />
                            <h3 className="text-lg font-semibold text-font-primary">Unsaved Changes</h3>
                        </div>
                        <p className="text-font-secondary mb-6">
                            You have unsaved changes. Are you sure you want to exit? All changes will be lost.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={handleCancelExit}
                                className="px-4 py-2 text-font-primary bg-bgSecondary rounded-lg hover:bg-accent hover:text-background transition-colors duration-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmExit}
                                className="px-4 py-2 text-font-primary bg-accent rounded-lg hover:bg-accent/80 transition-colors duration-300"
                            >
                                Exit Without Saving
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewEquipment;
